import { createLike, deleteReaction } from '@gik/api/activities/reactions';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useInkindCan } from '@gik/core/store/permissions';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { renderReactionIcon, renderReactionLabel } from '@gik/stream/utils/reactions';
import type { IButtonProps } from '@gik/ui/Button/ButtonProps';
import type { ReactionEmojiKey } from '@gik/ui/OpenMoji';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import { Button } from '../../../ui/Button';
import { Popover } from '../../../ui/Popover';
import { ReactionPicker } from './ReactionPicker';

export type onDeleteReactionCallback = (reactionId: string, name: ReactionEmojiKey) => void;
export type onReactionCallback = (forId: string, name: ReactionEmojiKey) => void;

export type ReactionButtonProps = {
  forId: string;
  mini?: boolean;
  hasIcon?: boolean;
  isReaction?: boolean;
  selected?: ReactionEmojiKey;
  selectedReactionId?: string;
  onDeleteReaction?: onDeleteReactionCallback;
  onDeleteReactionSuccess?: (forId: string, name: ReactionEmojiKey) => void;
  onReaction?: onReactionCallback;
  onReactionSuccess?: (forId: string, name: ReactionEmojiKey) => void;
} & IButtonProps;

function ReactionButtonComp({
  children,
  mini,
  className,
  forId,
  hasIcon,
  isReaction,
  selected,
  selectedReactionId,
  disabled,
  onDeleteReaction,
  onDeleteReactionSuccess,
  onReaction,
  onReactionSuccess,
  ...otherProps
}: React.PropsWithChildren<ReactionButtonProps>): React.ReactElement {
  const bem = useBemCN('reaction-button');

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const inkindRouteId = useInkindStore(store => store.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkind?.groupId);

  const handleReaction = React.useCallback(
    async (name: ReactionEmojiKey) => {
      onReaction?.(forId, name);
      setIsOpen(false);

      const newId = await createLike(forId, name, isReaction);
      if (!newId) {
        UI.notifyError('Failed to react. Please try again later.');
        return;
      }
      onReactionSuccess?.(forId, name);
    },
    [forId, isReaction, onReaction, onReactionSuccess]
  );

  const handleDeleteReaction = React.useCallback(
    async (name: ReactionEmojiKey) => {
      if (selectedReactionId) {
        onDeleteReaction?.(selectedReactionId, name);
        setIsOpen(false);

        const response = await deleteReaction(selectedReactionId, canEdit ? inkindRouteId : undefined);
        if (!response) {
          UI.notifyError('Failed to remove reaction. Please try again later.');
          return;
        }
        onDeleteReactionSuccess?.(selectedReactionId, name);
      }
    },
    [canEdit, inkindRouteId, onDeleteReaction, onDeleteReactionSuccess, selectedReactionId]
  );

  return (
    <Popover
      {...bem(null, null, className)}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      // hoverable
      triggerOnClick={() => {
        if (disabled) return;
        setIsOpen(true);
      }}
      // triggerOnMouseEnter={() => {
      //   if (disabled || !isMd) return;
      //   setIsOpen(true);
      // }}
      trigger={
        <Button
          noFocusStyling
          variant="white"
          prepend={hasIcon && renderReactionIcon(selected, mini)}
          uppercase={false}
          disabled={disabled}
          {...otherProps}
          {...bem(`btn`, [{ [`selected-${selected}`]: selected }, { default: !selected }])}
        >
          {renderReactionLabel(selected)}
        </Button>
      }
    >
      <ReactionPicker onSelect={handleReaction} onDelete={handleDeleteReaction} selected={selected} />
    </Popover>
  );
}

export const ReactionButton = withComponentErrorBoundary(ReactionButtonComp);
